import { LOG_BACKEND } from '@/graphql/mutations/backendLog';
import { apolloClient } from '@/modules/myStageGraphQL';
import logger from '@/modules/Logger';

const BATCH_THRESHOLD = 20;
const VISIBILITY_TIMEOUT = 20000; // 20 seconds

class BackendLogger {
  constructor() {
    this.apolloClient = apolloClient;
    this.buffer = [];
    this.visibilityTimer = null;

    this.handleBeforeUnload = this.onBeforeUnload.bind(this);
    this.handleVisibilityChange = this.onVisibilityChange.bind(this);

    window.addEventListener('beforeunload', this.handleBeforeUnload);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Append a log payload to the buffer.
   * Flushes immediately at threshold, or schedules a delayed flush if the page is hidden.
   * @param {Object} logInfo - { msg, op, res, sev, rsn }
   */
  send(logInfo) {
    this.buffer.push(logInfo);

    if (this.buffer.length >= BATCH_THRESHOLD) {
      this.flush();
    } else {
      this.scheduleHiddenFlush();
    }
  }

  /**
   * Flush all buffered logs to the backend via GraphQL.
   * Clears the buffer before awaiting the network call (fire-and-forget on failure).
   * @param {Object} [options]
   * @param {boolean} [options.keepalive=false] - Use keepalive fetch for page unload
   */
  async flush({ keepalive = false } = {}) {
    if (this.buffer.length === 0) {
      return;
    }

    const logsToSend = this.buffer.map((entry) => {
      if (entry.rsn == null) return entry;
      return { ...entry, rsn: JSON.stringify(entry.rsn) };
    });
    this.buffer = [];

    const mutateOptions = {
      mutation: LOG_BACKEND,
      variables: { input: logsToSend }
    };

    if (keepalive) {
      mutateOptions.context = { fetchOptions: { keepalive: true } };
    }

    try {
      await this.apolloClient.mutate(mutateOptions);
      logger.info('[BackendLogger] Flush successful', {
        operation: 'backend-logger',
        count: logsToSend.length
      });
    } catch (error) {
      if (!keepalive) {
        logger.error('[BackendLogger] Flush failed', { operation: 'backend-logger', error });
      }
    }
  }

  /**
   * Start a visibility timer if the page is hidden and no timer is already running.
   * When the timer fires, flushes any buffered logs.
   */
  scheduleHiddenFlush() {
    if (document.visibilityState !== 'hidden' || this.visibilityTimer !== null) {
      return;
    }

    this.visibilityTimer = setTimeout(() => {
      this.visibilityTimer = null;
      if (this.buffer.length > 0) {
        this.flush();
      }
    }, VISIBILITY_TIMEOUT);
  }

  /** Cancel any pending visibility timer. */
  cancelHiddenFlush() {
    if (this.visibilityTimer !== null) {
      clearTimeout(this.visibilityTimer);
      this.visibilityTimer = null;
    }
  }

  onBeforeUnload() {
    this.flush({ keepalive: true });
  }

  onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.scheduleHiddenFlush();
    } else {
      this.cancelHiddenFlush();
    }
  }
}

const backendLogger = new BackendLogger();

export default backendLogger;
export { BackendLogger };
